import { z } from 'zod';

import { CompareDiffSchema, IsoDateTimeSchema, RowErrorSchema, UuidSchema } from '../dto/common.js';
import {
  AlertStateSchema,
  CompareTriggerSchema,
  ConnectorKindSchema,
  ReplyClassSchema,
  RoleKeySchema,
  SourceSystemSchema,
} from '../enums.js';

import type { EventEnvelope } from './envelope.js';

// Versioned event contracts (plan Appendix D). Names are
// `<service>.<entity>.<pastTenseVerb>`. To change a payload incompatibly, add a new version
// and keep consumers of the old one working until every producer has moved.

export const EVENT_STREAMS = ['auth', 'source', 'compare', 'alert', 'messaging'] as const;
export type EventStream = (typeof EVENT_STREAMS)[number];

const contract = <TSchema extends z.ZodType>(
  stream: EventStream,
  version: number,
  payload: TSchema,
) => ({ stream, version, payload });

export const EVENT_CONTRACTS = {
  /** Demo simulator uses it for the prototype's login kickoff. */
  'auth.user.loggedIn': contract('auth', 1, z.object({ userId: UuidSchema, email: z.email() })),

  'source.snapshot.updated': contract(
    'source',
    1,
    z.object({
      assemblyId: UuidSchema,
      system: SourceSystemSchema,
      revisionId: UuidSchema,
      rev: z.string(),
      configuration: z.string(),
      /** Spec §5.1: a new released PDM revision is a "release" trigger. */
      isRelease: z.boolean(),
    }),
  ),

  /** BR-023, acceptance J: the whole file was rejected. Row detail is manager-only. */
  'source.read.failed': contract(
    'source',
    1,
    z.object({
      connectorId: UuidSchema,
      connectorKind: ConnectorKindSchema,
      errors: z.array(RowErrorSchema),
    }),
  ),

  'compare.run.requested': contract(
    'compare',
    1,
    z.object({
      assemblyId: UuidSchema,
      configuration: z.string().optional(),
      trigger: CompareTriggerSchema,
      requestedByUserId: UuidSchema.nullable(),
      alertId: UuidSchema.nullable(),
    }),
  ),

  'compare.run.completed': contract(
    'compare',
    1,
    z.object({
      runId: UuidSchema,
      assemblyId: UuidSchema,
      configuration: z.string(),
      trigger: CompareTriggerSchema,
      pdmRevisionId: UuidSchema,
      erpRevisionId: UuidSchema.nullable(),
      pdmRev: z.string(),
      erpRev: z.string().nullable(),
      /** BR-026: only set when both revisions are known and orderable. */
      revisionGap: z.number().int().nullable(),
      diffs: z.array(CompareDiffSchema),
    }),
  ),

  /** BR-023: a failed run publishes no diffs. */
  'compare.run.failed': contract(
    'compare',
    1,
    z.object({
      runId: UuidSchema,
      assemblyId: UuidSchema,
      configuration: z.string(),
      trigger: CompareTriggerSchema,
      errorDetail: z.object({ message: z.string(), errors: z.array(RowErrorSchema).optional() }),
    }),
  ),

  'alert.state.changed': contract(
    'alert',
    1,
    z.object({
      alertId: UuidSchema,
      code: z.string(),
      assemblyId: UuidSchema,
      from: AlertStateSchema.nullable(),
      to: AlertStateSchema,
      ownerUserId: UuidSchema.nullable(),
      version: z.number().int().nonnegative(),
    }),
  ),

  /** BR-029, BR-030: one approval of one exact draft version sends one email. */
  'alert.draft.approved': contract(
    'alert',
    1,
    z.object({
      draftId: UuidSchema,
      draftVersionId: UuidSchema,
      alertIds: z.array(UuidSchema).min(1),
      fromUserId: UuidSchema,
      fromEmail: z.email(),
      to: z.email(),
      subject: z.string().min(1),
      body: z.string().min(1),
    }),
  ),

  'alert.request.created': contract(
    'alert',
    1,
    z.object({
      requestId: UuidSchema,
      alertId: UuidSchema,
      requestedUserId: UuidSchema,
      requestedRole: RoleKeySchema,
      requesterUserId: UuidSchema,
    }),
  ),

  /**
   * "Recheck now", "I've fixed it", a Done request or a confirming reply (spec §4). The agent
   * worker re-reads PDM and ERP for the assembly, then asks compare-service for a `recheck`
   * run. Verification is always a source re-read, never the reply itself (BR-012).
   */
  'alert.recheck.requested': contract(
    'alert',
    1,
    z.object({
      alertId: UuidSchema,
      assemblyId: UuidSchema,
      configuration: z.string(),
      requestedByUserId: UuidSchema.nullable(),
    }),
  ),

  /**
   * An in-app notice for specific users: a failed detection run for the managers (BR-023),
   * a timeout or declined request for the owner, a reminder for a requested person.
   * messaging-service turns it into an in-app notification.
   */
  'alert.notice.raised': contract(
    'alert',
    1,
    z.object({
      userIds: z.array(UuidSchema).min(1),
      alertId: UuidSchema.nullable(),
      kind: z.enum(['run_failed', 'needs_attention', 'request_reminder']),
      text: z.string().min(1),
    }),
  ),

  'messaging.message.sent': contract(
    'messaging',
    1,
    z.object({
      outboundMessageId: UuidSchema,
      draftVersionId: UuidSchema,
      alertIds: z.array(UuidSchema).min(1),
      providerMessageId: z.string(),
      sentAt: IsoDateTimeSchema,
    }),
  ),

  /** Acceptance C: the alert stays in Draft ready with this error. */
  'messaging.message.failed': contract(
    'messaging',
    1,
    z.object({
      outboundMessageId: UuidSchema,
      draftVersionId: UuidSchema,
      alertIds: z.array(UuidSchema).min(1),
      error: z.string(),
    }),
  ),

  /** Acceptance H: one entry per alert on the thread, including `not_addressed`. */
  'messaging.reply.classified': contract(
    'messaging',
    1,
    z.object({
      replyId: UuidSchema,
      threadId: UuidSchema,
      fromEmail: z.email(),
      fromName: z.string().nullable(),
      receivedAt: IsoDateTimeSchema,
      classifications: z.array(
        z.object({ alertId: UuidSchema, class: ReplyClassSchema, evidence: z.string() }),
      ),
    }),
  ),
} as const;

export type EventType = keyof typeof EVENT_CONTRACTS;
export type EventPayload<T extends EventType> = z.infer<(typeof EVENT_CONTRACTS)[T]['payload']>;
export type DomainEvent<T extends EventType = EventType> = {
  [K in T]: EventEnvelope<K, EventPayload<K>>;
}[T];

export function streamFor(type: EventType): EventStream {
  return EVENT_CONTRACTS[type].stream;
}
